import { useEffect } from "react";
import { Header, Icon, Label, Modal, Table } from "semantic-ui-react";
import { useBooking } from "../../context/BookingContext";
import { fmtDate, fmtDateTime, fmtTime } from "./adminHelpers";

function FRow({ label, value }) {
  return (
    <Table.Row>
      <Table.Cell
        collapsing
        style={{ width: 150, color: "#6b7280", fontWeight: 500 }}
      >
        {label}
      </Table.Cell>
      <Table.Cell>{value || "—"}</Table.Cell>
    </Table.Row>
  );
}

function Section({ icon, title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <Header
        as="h5"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          borderBottom: "1.5px solid #e5e7eb",
          paddingBottom: 6,
        }}
      >
        <Icon name={icon} />
        <Header.Content>{title}</Header.Content>
      </Header>
      <Table basic="very" compact size="small">
        <Table.Body>{children}</Table.Body>
      </Table>
    </div>
  );
}

export default function BookingFormDetailModal({ booking, rooms, guestHouses, onClose }) {
  /* Viewing an indent counts as reading it. */
  const { markBookingSeen } = useBooking();
  useEffect(() => {
    markBookingSeen(booking);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);

  const room = rooms.find((r) => r.id === booking.roomId);
  const gh = guestHouses.find((g) => g.id === booking.guestHouseId);

  const apName = booking.applicantName || booking.userName || "—";
  const apEmpId = booking.employeeId || "—";
  const apDesig = booking.applicantDesignation || booking.designation || "—";
  const apDept = booking.applicantDepartment || booking.department || "—";
  const apMobile = booking.applicantMobileNumber || booking.phone || "—";
  const apEmail = booking.emailId || booking.email || "—";

  const gName = booking.guestName || "—";
  const gDesig = booking.guestDesignation || "—";
  const gMobile = booking.guestMobileNumber || "—";
  const gAddr = booking.guestAddress || "—";

  const ghName = gh?.name || booking.guestHouseName || "—";
  const roomNo = room?.roomNumber || booking.roomNumber || "Not yet assigned";
  const roomType = room?.roomType || booking.roomType || "—";

  const assignment =
    (booking.roomAssignments || []).find((a) => a.roomId === booking.roomId) ||
    (booking.roomAssignments || [])[0] ||
    null;
  const checkInValue = booking.arrivalTime
    ? `${fmtDate(booking.checkIn)} ${fmtTime(booking.arrivalTime)}`
    : fmtDate(booking.checkIn);
  const checkOutValue = booking.departureTime
    ? `${fmtDate(booking.checkOut)} ${fmtTime(booking.departureTime)}`
    : fmtDate(booking.checkOut);

  const accompanyingCount = booking.accompanyingPersonsCount || 0;

  return (
    <Modal open onClose={onClose} size="small" closeIcon className="bhu-booking-detail-modal">
      <Modal.Header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "14px 20px",
          background: "linear-gradient(135deg, #143a5a 0%, #1c5080 100%)",
          color: "#fff",
          fontFamily: '"Lora", Georgia, serif',
        }}
      >
        <Icon name="file alternate outline" style={{ color: "rgba(255,255,255,0.8)" }} />
        <span>Booking Details</span>
        <Label style={{ background: "rgba(255,255,255,0.18)", color: "#fff" }}>
          {booking.bookingCode}
        </Label>
      </Modal.Header>
      <Modal.Content scrolling style={{ padding: "16px 20px" }}>
        <Section icon="tag" title="Booking Info">
          <FRow label="Booking ID" value={booking.bookingCode} />
          <FRow label="Status" value={booking.status} />
          <FRow label="Guest House" value={ghName} />
          <FRow label="Room No" value={roomNo} />
          <FRow label="Room Type" value={roomType} />
          <FRow label="Check-in (Expected)" value={checkInValue} />
          {assignment?.actualCheckin && (
            <FRow label="Check-in (Actual)" value={fmtDateTime(assignment.actualCheckin)} />
          )}
          <FRow label="Check-out (Expected)" value={checkOutValue} />
          {assignment?.actualCheckout && (
            <FRow label="Check-out (Actual)" value={fmtDateTime(assignment.actualCheckout)} />
          )}
          <FRow
            label="Purpose"
            value={booking.purpose || booking.visitPurpose}
          />
          <FRow
            label="Payment"
            value={booking.payment || booking.paymentMode}
          />
        </Section>

        <Section icon="id card outline" title="Applicant Details">
          <FRow label="Name" value={apName} />
          <FRow label="Employee ID" value={apEmpId} />
          <FRow label="Designation" value={apDesig} />
          <FRow label="Department" value={apDept} />
          <FRow label="Mobile" value={apMobile} />
          <FRow label="Email" value={apEmail} />
        </Section>

        <Section icon="user outline" title="Guest Details">
          <FRow label="Guest Name" value={gName} />
          <FRow label="Designation" value={gDesig} />
          <FRow label="Mobile" value={gMobile} />
          <FRow label="Address" value={gAddr} />
        </Section>

        {accompanyingCount > 0 && (
          <Section icon="users" title="Accompanying Persons">
            <FRow label="Count" value={String(accompanyingCount)} />
          </Section>
        )}
      </Modal.Content>
    </Modal>
  );
}
